/**
 * Cookie Manager Utility
 * Handles cookie operations based on user consent preferences
 */

export interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

export interface CookieConsentData {
  preferences: CookiePreferences;
  timestamp: string;
  version: string;
}

class CookieManager {
  private static instance: CookieManager;
  private preferences: CookiePreferences | null = null;

  private constructor() {
    this.loadPreferences();
  }

  public static getInstance(): CookieManager {
    if (!CookieManager.instance) {
      CookieManager.instance = new CookieManager();
    }
    return CookieManager.instance;
  }

  /**
   * Load cookie preferences from localStorage
   */
  private loadPreferences(): void {
    try {
      const stored = localStorage.getItem('cookieConsent');
      if (stored) {
        const data: CookieConsentData = JSON.parse(stored);
        this.preferences = data.preferences;
      }
    } catch (error) {
      console.error('Failed to load cookie preferences:', error);
    }
  }

  /**
   * Get current cookie preferences
   */
  public getPreferences(): CookiePreferences | null {
    return this.preferences;
  }

  /**
   * Update cookie preferences
   */
  public updatePreferences(preferences: CookiePreferences): void {
    this.preferences = preferences;
    
    // Apply preferences (enable/disable cookies)
    this.applyPreferences();
  }

  /**
   * Check if a specific cookie category is allowed
   */
  public isAllowed(category: keyof CookiePreferences): boolean {
    if (!this.preferences) {
      // If no preferences set, only allow essential cookies
      return category === 'essential';
    }
    return this.preferences[category];
  }

  /**
   * Apply cookie preferences by enabling/disabling tracking scripts
   */
  private applyPreferences(): void {
    if (!this.preferences) return;

    // Handle Google Analytics
    if (this.preferences.analytics) {
      this.enableGoogleAnalytics();
    } else {
      this.disableGoogleAnalytics();
    }

    // Handle Marketing cookies
    if (this.preferences.marketing) {
      this.enableMarketingCookies();
    } else {
      this.disableMarketingCookies();
    }

    // Handle Functional cookies
    if (this.preferences.functional) {
      this.enableFunctionalCookies();
    } else {
      this.disableFunctionalCookies();
    }
  }

  /**
   * Enable Google Analytics
   */
  private enableGoogleAnalytics(): void {
    // Only enable if gtag is available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  }

  /**
   * Disable Google Analytics
   */
  private disableGoogleAnalytics(): void {
    // Disable GA tracking
    if (typeof window !== 'undefined') {
      // Set GA opt-out
      (window as any)['ga-disable-GA_MEASUREMENT_ID'] = true;
      
      // Update consent if gtag is available
      if ((window as any).gtag) {
        (window as any).gtag('consent', 'update', {
          'analytics_storage': 'denied'
        });
      }

      // Remove GA cookies
      this.deleteCookie('_ga');
      this.deleteCookie('_gid');
      this.deleteCookie('_gat');
      this.deleteCookie('_ga_*');
    }
  }

  /**
   * Enable marketing cookies
   */
  private enableMarketingCookies(): void {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted'
      });
    }
  }

  /**
   * Disable marketing cookies
   */
  private disableMarketingCookies(): void {
    if (typeof window !== 'undefined') {
      // Update consent if gtag is available
      if ((window as any).gtag) {
        (window as any).gtag('consent', 'update', {
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied'
        });
      }

      // Remove common marketing cookies
      this.deleteCookie('_fbp');
      this.deleteCookie('_fbc');
      this.deleteCookie('fr');
      this.deleteCookie('tr');
      this.deleteCookie('IDE');
      this.deleteCookie('test_cookie');
      this.deleteCookie('_gcl_au');
      this.deleteCookie('_gcl_aw');
      this.deleteCookie('_gcl_dc');
    }
  }

  /**
   * Enable functional cookies
   */
  private enableFunctionalCookies(): void {
    // Functional cookies are typically for user preferences
    // These are usually handled by the application itself
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        'functionality_storage': 'granted',
        'personalization_storage': 'granted'
      });
    }
  }

  /**
   * Disable functional cookies
   */
  private disableFunctionalCookies(): void {
    if (typeof window !== 'undefined') {
      // Update consent if gtag is available
      if ((window as any).gtag) {
        (window as any).gtag('consent', 'update', {
          'functionality_storage': 'denied',
          'personalization_storage': 'denied'
        });
      }

      // Remove functional cookies (except essential ones)
      this.deleteCookie('userPreferences');
      this.deleteCookie('theme');
      this.deleteCookie('language');
    }
  }

  /**
   * Set a cookie if the category is allowed
   */
  public setCookie(
    name: string,
    value: string,
    category: keyof CookiePreferences,
    days: number = 365
  ): boolean {
    if (!this.isAllowed(category)) {
      console.warn(`Cookie category "${category}" is not allowed. Cookie "${name}" was not set.`);
      return false;
    }

    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    
    // Set SameSite and Secure flags for better security
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax;Secure`;
    return true;
  }

  /**
   * Get a cookie value
   */
  public getCookie(name: string): string | null {
    const nameEQ = `${name}=`;
    const ca = document.cookie.split(';');
    
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    
    return null;
  }

  /**
   * Delete a cookie
   */
  public deleteCookie(name: string): void {
    // Delete cookie by setting expiry to past date
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    
    // Also try to delete with domain variations
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=${window.location.hostname};`;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=.${window.location.hostname};`;
    
    // Handle wildcard cookies (like _ga_*)
    if (name.includes('*')) {
      const prefix = name.replace('*', '');
      document.cookie.split(';').forEach(cookie => {
        const eqPos = cookie.indexOf('=');
        const cookieName = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        if (cookieName.startsWith(prefix)) {
          this.deleteCookie(cookieName);
        }
      });
    }
  }

  /**
   * Clear all non-essential cookies
   */
  public clearNonEssentialCookies(): void {
    const cookies = document.cookie.split(';');
    const essentialCookies = ['authToken', 'sessionId', 'csrfToken', 'refreshToken', 'cookieConsent'];
    
    cookies.forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      
      if (!essentialCookies.includes(name) && name) {
        this.deleteCookie(name);
      }
    });
  }

  /**
   * Check if user has made a cookie choice
   */
  public hasUserConsented(): boolean {
    return this.preferences !== null;
  }

  /**
   * Reset cookie preferences
   */
  public resetPreferences(): void {
    localStorage.removeItem('cookieConsent');
    this.preferences = null;
    this.clearNonEssentialCookies();
  }
}

export default CookieManager.getInstance();